import * as LabelPrimitive from '@radix-ui/react-label';
import { Slot } from '@radix-ui/react-slot';
import * as React from 'react';
import { Controller, type ControllerProps, type FieldPath, type FieldValues, FormProvider, useFormContext } from 'react-hook-form';

import { Label } from '@/components/ui/Label/Label';
import { cn } from '@/utils/index';

import { CircleAlert } from 'lucide-react';

const Form = FormProvider;

interface FormFieldContextValue<TFieldValues extends FieldValues = FieldValues, TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>> {
  name: TName;
}

const FormFieldContext = React.createContext<FormFieldContextValue>({} as FormFieldContextValue);

const FormField = <TFieldValues extends FieldValues = FieldValues, TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>>({
  ...props
}: ControllerProps<TFieldValues, TName>) => {
  const { name } = props;

  const value = React.useMemo(() => ({ name }), [name]);

  return (
    <FormFieldContext value={value}>
      <Controller {...props} />
    </FormFieldContext>
  );
};

const useFormField = () => {
  const fieldContext = React.use(FormFieldContext);
  const itemContext = React.use(FormItemContext);
  const { getFieldState, formState } = useFormContext();

  const fieldState = getFieldState(fieldContext.name, formState);

  if (!fieldContext) {
    throw new Error('useFormField should be used within <FormField>');
  }

  const { id } = itemContext;

  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState,
  };
};

interface FormItemContextValue {
  id: string;
  hasDescription: boolean;
  setHasDescription: (value: boolean) => void;
}

const FormItemContext = React.createContext<FormItemContextValue>({} as FormItemContextValue);

const FormItem = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => {
  const id = React.useId();
  const [hasDescription, setHasDescription] = React.useState(false);

  const value = React.useMemo(() => ({ id, hasDescription, setHasDescription }), [id, hasDescription]);

  return (
    <FormItemContext value={value}>
      <div className={cn('group/formitem flex w-full flex-col gap-1', className)} {...props} />
    </FormItemContext>
  );
};

const FormLabel = ({ className, ...props }: React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root>) => {
  const { formItemId } = useFormField();

  return <Label className={cn('text-text-default-secondary text-body-m-regular font-normal', className)} htmlFor={formItemId} {...props} />;
};

const FormControl = (props: React.ComponentPropsWithoutRef<typeof Slot>) => {
  const { error, formItemId, formDescriptionId, formMessageId } = useFormField();
  const { hasDescription } = React.use(FormItemContext);

  const getAriaDescribedBy = () => {
    const ids: string[] = [];

    if (hasDescription) {
      ids.push(formDescriptionId);
    }

    if (error) {
      ids.push(formMessageId);
    }

    return ids.length > 0 ? ids.join(' ') : undefined;
  };

  return <Slot id={formItemId} aria-describedby={getAriaDescribedBy()} aria-invalid={!!error} {...props} />;
};

const FormDescription = ({ className, asChild = false, ...props }: React.HTMLAttributes<HTMLParagraphElement> & { asChild?: boolean }) => {
  const { formDescriptionId } = useFormField();
  const { setHasDescription } = React.use(FormItemContext);

  React.useEffect(() => {
    setHasDescription(true);
    return () => setHasDescription(false);
  }, [setHasDescription]);

  const Comp = asChild ? Slot : 'p';

  return <Comp id={formDescriptionId} className={cn('text-text-default-secondary text-body-m-regular', className)} {...props} />;
};

const FormMessage = ({ className, children, showIcon, ...props }: React.HTMLAttributes<HTMLParagraphElement> & { showIcon?: boolean }) => {
  const { error, formMessageId } = useFormField();
  const body = error ? String(error?.message) : children;

  if (!body) {
    return null;
  }

  return (
    <p id={formMessageId} className={cn('text-text-error-primary text-body-m-regular flex items-center gap-1.5', className)} {...props} role='alert'>
      {showIcon && <CircleAlert className='size-4' />}
      {body}
    </p>
  );
};

export {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  // eslint-disable-next-line react-refresh/only-export-components
  useFormField,
};
